import React, { useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import styled from 'styled-components';
import Header from './components/Header';
import ParcelMap from './components/ParcelMap';
import FilterPanel from './components/FilterPanel';
import ParcelDetailsPanel from './components/ParcelDetailsPanel';
import FavoritesPanel from './components/FavoritesPanel';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import Register from './components/Register';
import UserManagement from './components/UserManagement';
import UserProfile from './components/UserProfile';
import GlobalToastContainer from './components/ToastContainer';
import { ParcelProvider } from './context/ParcelContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { ToastProvider } from './context/ToastContext';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const MapContainer = styled.div`
  flex: 1;
  position: relative;
`;

const SidebarContainer = styled.div`
  width: 400px;
  background: white;
  border-left: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DashboardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 1000;
  overflow: auto;
`;

const AuthenticatedApp = () => {
  const { user, logout } = useAuth();
  const [isParcelDetailsOpen, setIsParcelDetailsOpen] = useState(false);
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const [setMapViewState, setSetMapViewState] = useState(null);
  const [setParcelBorder, setSetParcelBorder] = useState(null);
  const [getMapBounds, setGetMapBounds] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  const handleToggleParcelDetails = () => {
    if (!isParcelDetailsOpen) {
      setIsParcelDetailsOpen(true);
      setIsFavoritesOpen(false);
      setIsFilterOpen(false);
      setIsDashboardOpen(false);
      setIsUserManagementOpen(false);
    }
    // If already open, do nothing
  };

  const handleToggleFavorites = () => {
    if (!isFavoritesOpen) {
      setIsFavoritesOpen(true);
      setIsParcelDetailsOpen(false);
      setIsFilterOpen(false);
      setIsDashboardOpen(false);
      setIsUserManagementOpen(false);
    }
    // If already open, do nothing
  };

  const handleViewStateChange = (setViewState) => {
    setSetMapViewState(() => setViewState);
  };

  const handleSetParcelBorder = (setParcelBorderFunc) => {
    setSetParcelBorder(() => setParcelBorderFunc);
  };

  const handleGetMapBounds = (getMapBoundsFunc) => {
    setGetMapBounds(() => getMapBoundsFunc);
  };

  const handleMapViewChange = (viewState) => {
    if (setMapViewState) {
      setMapViewState(viewState);
    }
  };

  const handleToggleFilter = () => {
    if (!isFilterOpen) {
      setIsFilterOpen(true);
      setIsFavoritesOpen(false);
      setIsParcelDetailsOpen(false);
      setIsDashboardOpen(false);
      setIsUserManagementOpen(false);
    }
    // If already open, do nothing
  };

  const handleToggleDashboard = () => {
    if (!isDashboardOpen) {
      setIsDashboardOpen(true);
      setIsFavoritesOpen(false);
      setIsParcelDetailsOpen(false);
      setIsFilterOpen(false);
      setIsUserManagementOpen(false);
    }
    // If already open, do nothing
  };

  const handleToggleUserManagement = () => {
    if (!isUserManagementOpen) {
      setIsUserManagementOpen(true);
      setIsFavoritesOpen(false);
      setIsParcelDetailsOpen(false);
      setIsFilterOpen(false);
      setIsDashboardOpen(false);
      setIsUserProfileOpen(false);
    }
    // If already open, do nothing
  };

  const handleToggleUserProfile = () => {
    if (!isUserProfileOpen) {
      setIsUserProfileOpen(true);
      setIsFavoritesOpen(false);
      setIsParcelDetailsOpen(false);
      setIsFilterOpen(false);
      setIsDashboardOpen(false);
      setIsUserManagementOpen(false);
    }
    // If already open, do nothing
  };

  return (
    <ParcelProvider>
      <FavoritesProvider>
        <Router>
          <AppContainer>
        <Header 
          onToggleParcelDetails={handleToggleParcelDetails}
          isParcelDetailsOpen={isParcelDetailsOpen}
          onToggleFavorites={handleToggleFavorites}
          isFavoritesOpen={isFavoritesOpen}
          onToggleFilter={handleToggleFilter}
          isFilterOpen={isFilterOpen}
          onToggleDashboard={handleToggleDashboard}
          isDashboardOpen={isDashboardOpen}
          onToggleUserManagement={handleToggleUserManagement}
          isUserManagementOpen={isUserManagementOpen}
          onToggleUserProfile={handleToggleUserProfile}
          isUserProfileOpen={isUserProfileOpen}
          user={user}
          onLogout={logout}
        />
        <MainContent>
        <MapContainer>
            <ParcelMap 
              onViewStateChange={handleViewStateChange}
              onSetParcelBorder={handleSetParcelBorder}
              onGetMapBounds={handleGetMapBounds}
            />
          </MapContainer>
          {!isDashboardOpen && !isUserManagementOpen && !isUserProfileOpen && (
            <SidebarContainer>
              {isParcelDetailsOpen ? (
                <ParcelDetailsPanel 
                  isOpen={true}
                  onClose={() => setIsParcelDetailsOpen(false)}
                  setViewState={setMapViewState}
                />
              ) : isFavoritesOpen ? (
                <FavoritesPanel 
                  onFavoriteClick={setMapViewState}
                  onSetParcelBorder={setParcelBorder}
                />
              ) : isFilterOpen ? (
                <FilterPanel onGetMapBounds={getMapBounds} />
              ) : null}
            </SidebarContainer>
          )}
          {isDashboardOpen && (
            <DashboardOverlay>
              <Dashboard onClose={() => {
                setIsDashboardOpen(false);
                setIsFilterOpen(true);
              }} />
            </DashboardOverlay>
          )}
          {isUserManagementOpen && (
            <DashboardOverlay>
              <UserManagement onClose={() => {
                setIsUserManagementOpen(false);
                setIsFilterOpen(true);
              }} />
            </DashboardOverlay>
          )}
          {isUserProfileOpen && (
            <DashboardOverlay>
              <UserProfile onClose={() => {
                setIsUserProfileOpen(false);
                setIsFilterOpen(true);
              }} />
            </DashboardOverlay>
          )}
        </MainContent>
      </AppContainer>
    </Router>
      </FavoritesProvider>
  </ParcelProvider>
  );
};

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const [showLogin, setShowLogin] = useState(true);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ color: 'white', textAlign: 'center' }}>
          <div style={{ 
            display: 'inline-block',
            width: '32px',
            height: '32px',
            border: '3px solid rgba(255,255,255,0.3)',
            borderTop: '3px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return showLogin ? (
      <Login onSwitchToRegister={() => setShowLogin(false)} />
    ) : (
      <Register onSwitchToLogin={() => setShowLogin(true)} />
    );
  }

  return <AuthenticatedApp />;
};

const App = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <GlobalToastContainer />
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
};

export default App; 